/**
 * GSTR-1A on the Returns screen (FR-E13, B-07): what a filed month offers, in words.
 *
 * Two questions a screen has to answer, kept out of `Periods.tsx` so a test can hold them:
 * whether a filed GSTR-1 should offer *Open a GSTR-1A for this month* (only where a change
 * exists and none is open yet — a button that can only refuse is noise), and what one line
 * should say about the correction's contents. The service computed every count; this names
 * them.
 */

import type { Gstr1aBlock, PeriodRecord } from './api'

export type Gstr1aOffer =
  /** Not a filed GSTR-1 ProGST prepared, or the service sent no block. */
  | { kind: 'none' }
  /** No filed side to compare against — marked filed on an ARN alone, nothing sent. */
  | { kind: 'unavailable'; words: string }
  /** Nothing has changed since filing; no 1A exists. */
  | { kind: 'nothing'; words: string }
  /** A change exists and no 1A is open: offer the button. */
  | { kind: 'open'; words: string }
  /** A 1A is open for this month: point at it. */
  | { kind: 'exists'; words: string; gstr1aPeriodId: string }

function count(n: number, one: string, many: string): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`
}

/** "1 document amended, 2 added and 1 filed document missing from the books". */
export function changeWords(block: Gstr1aBlock): string {
  const parts: string[] = []
  const amended = block.amended?.length ?? 0
  const added = block.added?.length ?? 0
  const deleted = block.deleted?.length ?? 0
  if (amended) parts.push(`${count(amended, 'document', 'documents')} amended`)
  if (added) parts.push(`${count(added, 'document', 'documents')} added`)
  if (deleted)
    parts.push(`${count(deleted, 'filed document', 'filed documents')} missing from the books`)
  if (parts.length === 0) return "Nothing has changed since this month's GSTR-1 was filed."
  if (parts.length === 1) return `${parts[0]} since this month's GSTR-1 was filed.`
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]} since this month's GSTR-1 was filed.`
}

/** What a filed GSTR-1's panel offers about its 1A. */
export function gstr1aOffer(period: PeriodRecord, block: Gstr1aBlock | undefined): Gstr1aOffer {
  if (
    !block ||
    period.return_type !== 'GSTR1' ||
    period.origin === 'snapshot' ||
    !['filed', 'locked'].includes(period.state)
  )
    return { kind: 'none' }
  if (block.gstr1a_period_id) {
    return {
      kind: 'exists',
      gstr1aPeriodId: block.gstr1a_period_id,
      words: block.available
        ? `A GSTR-1A is open for this month. ${changeWords(block)}`
        : 'A GSTR-1A is open for this month.',
    }
  }
  if (!block.available) {
    return {
      kind: 'unavailable',
      words: block.reason ?? 'Nothing is held for what was filed, so there is nothing to compare.',
    }
  }
  if (!block.changes) return { kind: 'nothing', words: changeWords(block) }
  if (period.state === 'locked') {
    return {
      kind: 'unavailable',
      words: `${changeWords(block)} This month is locked, so the change goes through an amendment in a later month.`,
    }
  }
  return { kind: 'open', words: changeWords(block) }
}

/** Where the filed side came from, as the card says it. */
export function filedSourceWords(source: string | null | undefined): string {
  if (source === 'filed_return') return "against the portal's own filed return"
  if (source === 'gstn_json') return 'against the GSTN JSON ProGST sent (no portal download held yet)'
  return ''
}
